"use client";

import {
    ForwardRefExoticComponent,
    RefAttributes,
    SyntheticEvent,
    VideoHTMLAttributes,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useRuntimeConfig } from "@finbackoffice/site-core";
import Loading from "../loading/Loading";

type IProps = VideoHTMLAttributes<HTMLVideoElement> & {
    src: string;
    loopWithDelay?: boolean;
    onReady?: () => void;
    prefixAssetsUrl?: boolean;
};

const DELAY = 3000;

const Video: ForwardRefExoticComponent<IProps & RefAttributes<HTMLVideoElement>> = forwardRef(
    (
        {
            src,
            muted = true,
            prefixAssetsUrl = true,
            loopWithDelay,
            loop = !loopWithDelay,
            onEnded,
            onError,
            onReady,
            ...rest
        },
        ref,
    ) => {
        const ASSETS_URL = useRuntimeConfig("ASSETS_URL");

        const fullSrc = useMemo(
            () => (prefixAssetsUrl ? `${ASSETS_URL}${src}` : src),
            [prefixAssetsUrl, ASSETS_URL, src],
        );

        const [loading, setLoading] = useState(true);
        const [hasError, setHasError] = useState(false);

        const timeoutRef = useRef<NodeJS.Timeout | null>(null);
        const videoRef = useRef<HTMLVideoElement | null>(null);
        const loadingRef = useRef(true);

        const setVideoRef = useCallback(
            (node: HTMLVideoElement | null) => {
                videoRef.current = node;
                if (typeof ref === "function") {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref],
        );

        const onLoadCallback = useCallback(() => {
            if (loadingRef.current) {
                loadingRef.current = false;
                setLoading(false);
                onReady?.();
            }
        }, [onReady]);

        const handleError = useCallback(
            (e?: SyntheticEvent<HTMLVideoElement>) => {
                loadingRef.current = false;
                setLoading(false);
                setHasError(true);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                if (e) {
                    onError?.(e);
                } else {
                    // Call onError even if no event, to trigger fallback
                    onError?.({} as SyntheticEvent<HTMLVideoElement>);
                }
            },
            [onError],
        );

        const handleStalled = useCallback(() => {
            const video = videoRef.current;
            if (video) {
                if (
                    video.error !== null ||
                    video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
                ) {
                    handleError();
                }
            }
        }, [handleError]);

        const onEndedHandler = useCallback(
            (e: SyntheticEvent<HTMLVideoElement>) => {
                if (loopWithDelay) {
                    const video = e.currentTarget;

                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => {
                        void video.play();
                    }, DELAY);
                }

                onEnded?.(e);
            },
            [loopWithDelay, onEnded],
        );

        useEffect(() => {
            loadingRef.current = true;
            setLoading(true);
            setHasError(false);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, [fullSrc]);

        // Error detection
        useEffect(() => {
            if (!loading || hasError) return;

            const video = videoRef.current;
            if (video?.error !== null) {
                handleError();
            }
        }, [loading, hasError, handleError]);

        if (!src.includes("mp4")) {
            return null;
        }

        if (hasError) {
            return null;
        }

        return (
            <>
                {loading && <Loading />}
                <video
                    ref={setVideoRef}
                    src={fullSrc}
                    autoPlay
                    playsInline
                    muted={muted}
                    loop={loop}
                    onPlay={onLoadCallback}
                    onEnded={onEndedHandler}
                    onError={handleError}
                    onStalled={handleStalled}
                    {...rest}
                    onLoadedData={onLoadCallback}></video>
            </>
        );
    },
);

export default Video;
